import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { addCouponReducer, removeCouponReducer, updateCouponReducer } from "../reducers";

export const fetchCoupons = createAsyncThunk(
    "coupons/fetchAll",
    async () => {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/coupons`);
        const coupons = await res.json();

        if (!coupons.length) return {};

        const ids = coupons.map(c => c.id).join(",");
        const ratingsMap = await fetch(`${process.env.REACT_APP_API_URL}/api/ratings/coupons?ids=${ids}`)
            .then(r => r.json())
            .catch(() => ({}));

        const map = {};
        coupons.forEach(c => {
            map[c.id] = {
                ...c,
                average_rating: ratingsMap[c.id]?.average ?? null,
                rating_count: ratingsMap[c.id]?.count ?? 0,
            };
        });
        return map;
    },
    {
        condition: (_, { getState }) => {
            return getState().coupons.status !== "loading";
        },
    }
);

export const fetchCouponsByVendor = createAsyncThunk(
    "coupons/fetchByVendor",
    async (vendorId) => {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/coupons/vendor/${vendorId}`);
        const coupons = await res.json();

        if (!coupons.length) return { map: {}, list: [] };

        const ids = coupons.map(c => c.id).join(",");
        const ratingsMap = await fetch(`${process.env.REACT_APP_API_URL}/api/ratings/coupons?ids=${ids}`)
            .then(r => r.json())
            .catch(() => ({}));

        const map = {};
        const list = [];
        coupons.forEach(c => {
            const enriched = {
                ...c,
                average_rating: ratingsMap[c.id]?.average ?? null,
                rating_count: ratingsMap[c.id]?.count ?? 0,
            };
            map[c.id] = enriched;
            list.push(enriched);
        });
        return { map, list };
    }
);

export const fetchFeaturedCoupons = createAsyncThunk(
    "coupons/fetchFeatured",
    async () => {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/coupons/get_featured_coupons`);
        return await res.json();
    }
);

export const fetchTopTwoCoupons = createAsyncThunk(
    "coupons/fetchTopTwo",
    async () => {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/coupons/top-two-coupons`);
        return await res.json();
    },
    {
        condition: (_, { getState }) => {
            return getState().coupons.topTwoCouponsStatus !== "loading";
        },
    }
);

export const deleteCouponById = createAsyncThunk(
    "coupons/deleteById",
    async (couponId) => {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/coupons/${couponId}`, {
            method: "DELETE",
        });
        if (!res.ok) {
            const data = await res.json();
            throw new Error(data.error || "Failed to delete coupon");
        }
        return couponId;
    }
);

export const toggleCouponActive = createAsyncThunk(
    "coupons/toggleActive",
    async (couponId) => {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/coupons/${couponId}/toggle-active`, {
            method: "PATCH",
        });
        if (!res.ok) {
            const data = await res.json();
            throw new Error(data.error || "Failed to toggle coupon");
        }
        return await res.json();
    }
);

export const fetchCouponById = createAsyncThunk(
    "coupons/fetchById",
    async (id) => {
        const [detail, ratingData] = await Promise.all([
            fetch(`${process.env.REACT_APP_API_URL}/api/coupons/${id}`).then(r => r.json()),
            fetch(`${process.env.REACT_APP_API_URL}/api/ratings/coupon/${id}`).then(r => r.json()).catch(() => ({})),
        ]);
        return { id, detail, rating: { average: ratingData.average ?? null, count: ratingData.count ?? 0 } };
    }
);

const initialState = {
    coupons: {},
    status: "idle",
    error: null,
    featuredCoupons: [],
    featuredCouponsStatus: "idle",
    topTwoCoupons: [],
    topTwoCouponsStatus: "idle",
    couponDetails: {},
    couponRatings: {},
    vendorCoupons: [],
    vendorCouponsStatus: "idle",
};

const couponSlice = createSlice({
    name: "coupons",
    initialState,
    reducers: {
        addCoupon: addCouponReducer,
        removeCoupon: removeCouponReducer,
        updateCoupon: updateCouponReducer,
        resetCoupons: (state) => {
            state.coupons = {};
            state.status = "idle";
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCoupons.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchCoupons.fulfilled, (state, action) => {
                state.coupons = action.payload;
                state.status = "succeeded";
            })
            .addCase(fetchCoupons.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchCouponsByVendor.pending, (state) => {
                state.vendorCouponsStatus = "loading";
            })
            .addCase(fetchCouponsByVendor.fulfilled, (state, action) => {
                state.vendorCoupons = action.payload.list;
                state.vendorCouponsStatus = "succeeded";
            })
            .addCase(fetchCouponsByVendor.rejected, (state) => {
                state.vendorCouponsStatus = "failed";
            })
            .addCase(fetchFeaturedCoupons.pending, (state) => {
                state.featuredCouponsStatus = "loading";
            })
            .addCase(fetchFeaturedCoupons.fulfilled, (state, action) => {
                state.featuredCoupons = action.payload;
                state.featuredCouponsStatus = "succeeded";
            })
            .addCase(fetchFeaturedCoupons.rejected, (state) => {
                state.featuredCouponsStatus = "failed";
            })
            .addCase(fetchTopTwoCoupons.pending, (state) => {
                state.topTwoCouponsStatus = "loading";
            })
            .addCase(fetchTopTwoCoupons.fulfilled, (state, action) => {
                state.topTwoCoupons = action.payload;
                state.topTwoCouponsStatus = "succeeded";
            })
            .addCase(fetchTopTwoCoupons.rejected, (state) => {
                state.topTwoCouponsStatus = "failed";
            })
            .addCase(fetchCouponById.fulfilled, (state, action) => {
                const { id, detail, rating } = action.payload;
                state.couponDetails[id] = detail;
                state.couponRatings[id] = rating;
            })
            .addCase(deleteCouponById.fulfilled, (state, action) => {
                const id = action.payload;
                state.vendorCoupons = state.vendorCoupons.filter((c) => c.id !== id);
                delete state.coupons[id];
            })
            .addCase(toggleCouponActive.fulfilled, (state, action) => {
                const { id, active } = action.payload;
                const inList = state.vendorCoupons.find((c) => c.id === id);
                if (inList) inList.active = active;
                if (state.coupons[id]) state.coupons[id].active = active;
            });
    },
});

export const { addCoupon, removeCoupon, updateCoupon, resetCoupons } = couponSlice.actions;
export default couponSlice.reducer;
